/**
 * Tokens extraídos do mockup de design (`mockups/
 * Kory RevOps Hub - Fila.dc.html`), que é a fonte de verdade visual — o PRD §13
 * exige que a "pele" venha do anexo de marca, não de suposição do implementador.
 * Valores aqui são cópia literal do mockup; não inventar tons novos.
 *
 * Etapa 2 do plano de UX/UI (2026-09-10, ver `~/Desktop/Kory RevOps Hub -
 * Plano UX-UI e Modo Claro.md`): `C.*`/`ink()` deixaram de ser hex/rgba
 * fixos e passaram a referenciar variáveis CSS (`--color-*`/`--ink-rgb`,
 * definidas em `styles.css` pra `:root` — escuro, padrão — e
 * `[data-theme="light"]`). Isso é o que permite os +100 usos de `C.bg`/
 * `ink(a)` espalhados pelo projeto responderem ao toggle de tema sem
 * precisar editar cada componente um a um — o navegador resolve o `var()`
 * de novo sozinho quando o atributo `data-theme` muda, sem re-render do
 * React. Continua valendo a regra de nunca inventar hex: qualquer token
 * novo entra como variável nos dois modos em `styles.css`, não como valor
 * solto aqui.
 */

/** Alfa sobre o texto-base do tema ativo — toda superfície/texto secundário sai daqui. */
export const ink = (a: number) => `rgba(var(--ink-rgb), ${a})`;

/**
 * Escala de contraste nomeada pro texto secundário (Etapa 1 do plano de
 * UX/UI, 2026-09-10 — achado real: 111 ocorrências de `ink(0.3–0.5x)`
 * escolhidas "de olho" no código, cada uma com um alfa ligeiramente
 * diferente pro MESMO papel visual, o que deixava rótulo de KPI
 * inconsistente entre telas e sistematicamente apagado demais. Um
 * componente novo usa esta escala em vez de escolher um alfa novo.
 */
export const text = {
  /** Rótulo que precisa ancorar a leitura — nome de um KPI, cabeçalho de bloco/chip. */
  title: ink(0.72),
  /** Corpo secundário que carrega dado real (subtítulo com valor, nota, contagem). */
  body: ink(0.58),
  /** Legenda discreta de propósito — timestamp, hint, texto de apoio sem dado. */
  muted: ink(0.4),
  /** Desabilitado/placeholder. */
  disabled: ink(0.24),
} as const;

export const C = {
  bg: 'var(--color-bg)',
  bgDeep: 'var(--color-bg-deep)',
  bgLift: 'var(--color-bg-lift)',
  fg: 'var(--color-fg)',
  accent: 'var(--color-accent)',
  accentStrong: 'var(--color-accent-strong)',
  accentDeep: 'var(--color-accent-deep)',
  churn: 'var(--color-churn)',
  amber: 'var(--color-amber)',
  green: 'var(--color-green)',
  greenDeep: 'var(--color-green-deep)',
  blue: 'var(--color-blue)',
  blueSoft: 'var(--color-blue-soft)',
  violet: 'var(--color-violet)',
} as const;

export const PAGE_BG = 'var(--page-bg)';

export const FONT =
  "-apple-system,BlinkMacSystemFont,'SF Pro Text','Segoe UI',Roboto,Helvetica,Arial,sans-serif";

/** Superfície padrão de card (mockup: painéis de KPI, gráficos, listas). */
export const surface = {
  background: ink(0.045),
  border: `1px solid ${ink(0.09)}`,
  borderRadius: 22,
} as const;

/** Popover/menu flutuante — vidro escuro com blur, usado em pills, dropdowns e modais. */
export const glass = {
  background: 'var(--glass-bg)',
  backdropFilter: 'blur(26px) saturate(1.5)',
  WebkitBackdropFilter: 'blur(26px) saturate(1.5)',
  border: `1px solid ${ink(0.13)}`,
  boxShadow: 'var(--glass-shadow)',
} as const;

export type Zone = 'PowerZap' | 'PowerBot' | 'Kory';

/**
 * Chip de zona — remapeado por pedido explícito do Rodrigo (não é mais cópia
 * literal do mockup original): PowerZap = verde, PowerBot = azul, Kory =
 * laranja. Cores continuam vindo só da paleta já existente (`C.*`), nenhum
 * hex novo inventado.
 */
export const ZONE: Record<Zone, { bg: string; color: string }> = {
  PowerZap: { bg: 'rgba(46,204,113,0.16)', color: C.green },
  PowerBot: { bg: 'rgba(74,125,255,0.18)', color: C.blueSoft },
  Kory: { bg: 'rgba(255,107,71,0.16)', color: C.accent },
};

export function zoneStyle(zone: string | null | undefined) {
  return ZONE[(zone as Zone) ?? 'Kory'] ?? ZONE.Kory;
}

/** churn = vermelho, contraction = âmbar (PRD §5: borda lateral do card). */
export function typeStyle(eventType: string) {
  return eventType === 'churn'
    ? { color: C.churn, bg: 'rgba(242,85,90,0.14)' }
    : { color: C.amber, bg: 'rgba(245,165,36,0.14)' };
}

export const EASE_SPRING = 'cubic-bezier(0.34,1.4,0.34,1)';

/**
 * Altura do header (App.tsx) — fonte única pros offsets `top` de tudo que é
 * `position: sticky` abaixo dele (FilterBar, AppSidebar, o cartão de
 * Auditados da Fila). Mudar aqui em vez de caçar número mágico em 4 arquivos
 * toda vez que o header mudar de tamanho.
 */
export const HEADER_HEIGHT = 82;
